import { FC, useCallback, useEffect, useMemo, useState, KeyboardEvent } from 'react';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { SearchInput } from 'components/Search/SearchInput';
import { useDebounce } from 'react-use';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { getExplorePageNameByPath } from 'utils/urls/pageName';
import { useCurrentStore } from 'modules/Listings/store/useCurrentStore';
import { LISTINGS_DEBOUNCE_FETCH_MS } from 'modules/Listings/store/models/constants';
import { wrapper } from './CommunitySearchMenu_styles';

const PLACEHOLDER_MAPPING = new Map([
  ['/explore/apps', 'Search by app ID, description, notes or user ID'],
  ['/explore/datasets', 'Search by datasets ID, description, notes or user ID'],
  ['/explore/models', 'Search by model ID, description, notes or user ID'],
  ['/explore/workflows', 'Search by workflow ID, description, notes or user ID'],
  ['/explore/modules', 'Search by module ID, description or user ID'],
]);

export const CommunitySearchMenu: FC = observer(() => {
  const currentStore = useCurrentStore();
  const { searchTermValue, setSearchTermValue, isSearchable } = currentStore;
  const [searchText, setSearchText] = useState(searchTermValue);
  const { pathname } = useRouter();
  const { track } = useDataCollection();

  const placeholder = useMemo(() => PLACEHOLDER_MAPPING.get(pathname) || 'Search Community', [pathname]);

  // `useCurrentStore` dynamically returns the store based on user selection.
  // While searching the input, sometimes, even though the store is updated,
  // other parts of the application using `useMemo` do not reflect the latest store update.
  // To fix this, we are passing the current store as a dependency to `useMemo`.
  const debouncedListingSearch = useMemo(() => debounce(setSearchTermValue, LISTINGS_DEBOUNCE_FETCH_MS), [currentStore]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const updateSearchText = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedListingSearch(text);
    },
    [setSearchText, debouncedListingSearch],
  );

  // This makes sure that "searchText" and "searchTermValue" remains in sync when user goes back to previous page
  useEffect(() => {
    if (searchText !== searchTermValue) {
      setSearchText(searchTermValue);
    }
  }, [searchTermValue]);

  useDebounce(
    () => {
      if (searchTermValue) {
        track('Community Model Searched', { param1: getExplorePageNameByPath(pathname), text: searchTermValue });
      }
    },
    1000,
    [searchTermValue],
  );

  return isSearchable ? (
    <div className={wrapper} onKeyDown={handleKeyDown} role="presentation">
      <SearchInput value={searchText} placeholder={placeholder} setValue={updateSearchText} isClearable />
    </div>
  ) : null;
});
